// Expiración de sesión por inactividad (Req. duración de sesión estándar).
//
// Define un tiempo máximo de inactividad (2 h por defecto): si el usuario no
// interactúa en ese periodo, la sesión se inhabilita. Cualquier interacción
// (ratón, teclado, scroll, toque, clic) RENUEVA la cuenta atrás. Al agotarse el
// tiempo se invoca `onIdle` (típicamente `signOut`), lo que a su vez provoca el
// redirect automático al home (lo gobierna `RequireAuth` en App.tsx).
//
// Detalles de robustez:
//  - La marca de última actividad se persiste en `localStorage` para que el
//    límite sobreviva a recargas de página y se SINCRONICE entre pestañas: si
//    el usuario interactúa en otra pestaña, todas renuevan; si una cierra la
//    sesión, el resto lo detecta por el evento `storage`.
//  - Un chequeo periódico (y al volver a enfocar/visualizar la pestaña) detecta
//    el vencimiento aunque la pestaña estuviera en segundo plano, donde los
//    `setTimeout` largos pueden posponerse.

import { useCallback, useEffect, useRef } from 'react';

/** Tiempo máximo de inactividad por defecto: 2 horas (en milisegundos). */
export const DEFAULT_IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;

/** Clave en localStorage para la marca de última actividad (compartida entre pestañas). */
export const LAST_ACTIVITY_KEY = 'vinilos.lastActivity';

/** Eventos del usuario que cuentan como "actividad" y renuevan la sesión. */
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousedown',
  'mousemove',
  'keydown',
  'wheel',
  'scroll',
  'touchstart',
  'click',
];

/**
 * Intervalo mínimo entre persistencias de la marca de actividad. Evita escribir
 * en localStorage en cada `mousemove`; basta con refrescar como mucho una vez
 * por minuto para un presupuesto de inactividad de horas.
 */
const PERSIST_THROTTLE_MS = 60 * 1000;

/** Cada cuánto se comprueba si venció el tiempo de inactividad. */
const CHECK_INTERVAL_MS = 30 * 1000;

function now(): number {
  return Date.now();
}

function readLastActivity(): number {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const value = raw ? Number(raw) : NaN;
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function writeLastActivity(ts: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  } catch {
    // Si localStorage no está disponible, el timer en memoria sigue funcionando.
  }
}

/**
 * Instala el control de inactividad mientras `enabled` sea verdadero.
 *
 * @param enabled  Activa el control (p. ej. solo cuando hay sesión).
 * @param onIdle   Acción al vencer el tiempo (p. ej. cerrar sesión).
 * @param timeoutMs  Tiempo máximo de inactividad (por defecto 2 h).
 */
export function useIdleTimeout(
  enabled: boolean,
  onIdle: () => void,
  timeoutMs: number = DEFAULT_IDLE_TIMEOUT_MS,
): void {
  // Guardamos `onIdle` en una ref para no reinstalar los listeners cada render
  // (la identidad de la función puede cambiar entre renders).
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  // Evita disparar `onIdle` más de una vez por ciclo de expiración.
  const firedRef = useRef(false);
  const lastPersistRef = useRef(0);

  const expire = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    onIdleRef.current();
  }, []);

  useEffect(() => {
    if (!enabled) return;

    firedRef.current = false;
    lastPersistRef.current = 0;

    // Al (re)activarse, la actividad empieza "ahora": renueva el presupuesto
    // completo en vez de heredar una marca antigua que podría expirar al vuelo.
    const start = now();
    writeLastActivity(start);
    lastPersistRef.current = start;

    const markActivity = () => {
      if (firedRef.current) return;
      const t = now();
      // Persistimos como mucho una vez por `PERSIST_THROTTLE_MS`.
      if (t - lastPersistRef.current >= PERSIST_THROTTLE_MS) {
        writeLastActivity(t);
        lastPersistRef.current = t;
      }
    };

    const checkExpiry = () => {
      if (firedRef.current) return;
      const last = readLastActivity() || lastPersistRef.current;
      if (now() - last >= timeoutMs) expire();
    };

    // Sincronización entre pestañas: si otra pestaña renueva la actividad,
    // reflejamos la marca más reciente; si otra la limpia, dejamos que el
    // chequeo periódico decida.
    const onStorage = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
        const t = Number(e.newValue);
        if (Number.isFinite(t)) lastPersistRef.current = Math.max(lastPersistRef.current, t);
      }
    };

    // Al volver a enfocar/mostrar la pestaña, comprobamos de inmediato: durante
    // segundo plano los timers pueden posponerse y la sesión pudo vencer.
    const onFocusOrVisible = () => {
      if (document.visibilityState === 'visible') checkExpiry();
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, markActivity, { passive: true });
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);

    const interval = window.setInterval(checkExpiry, CHECK_INTERVAL_MS);

    return () => {
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, markActivity);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
      window.clearInterval(interval);
    };
  }, [enabled, timeoutMs, expire]);
}
